/**
 * Pure helpers for the audit log surfaces (`GET /api-logs`, `/stats`, `/:id`).
 *
 * Kept in `lib/` and free of React so the list, the detail drawer and the stats
 * panel can all share one reading of the contract's traps — the deleted-actor
 * row, the capped total, the inclusive `to` bound and the wholesale body
 * truncation marker are each easy to get subtly wrong in three places at once.
 */
import type { BadgeTone } from '@/components/ui/Badge';
import { todayIsoDate } from '@/lib/invoice-utils';
import { addDaysIso } from '@/lib/quote-utils';
import type { ApiLogActor } from '@/lib/types';

/**
 * The states a SINGLE person on an audit row can be in, made explicit.
 *
 * A plain `ApiLogActor | null` invites `actor?.name ?? 'Anonymous'`, which
 * silently merges two facts an audit exists to keep apart: "nobody was signed
 * in" and "somebody was signed in and their account has since been deleted".
 * Discriminating on `kind` forces the caller to name all four.
 */
export type SimpleActorState =
  | { kind: 'anonymous' }
  | { kind: 'user'; id: number; name: string; email: string | null }
  | { kind: 'deleted'; email: string }
  | { kind: 'unknown' };

/**
 * Every state an audit row's actor can be in — the four single-person states
 * above, plus the one where TWO people are behind one request.
 *
 * A support session lets a super admin act as another account for 30 minutes,
 * with full access including deletes. The owner accepted that on one condition:
 * every action taken inside one must be attributable to BOTH people, never to
 * the target alone. This union is where that condition is kept — a support
 * session is its own `kind`, so a caller that switches on `kind` cannot render
 * one as an ordinary action without deleting a branch on purpose.
 *
 * It is deliberately NOT a flag on the `'user'` state. A boolean is ignorable:
 * every existing `case 'user':` would keep compiling and keep printing one name,
 * and the audit trail would become a silent forgery with nothing failing.
 */
export type ActorState =
  | SimpleActorState
  | {
      kind: 'support-session';
      /** The person who was acting — the super admin who opened the session. */
      operator: SimpleActorState;
      /** The account they were acting in. */
      target: SimpleActorState;
    };

/**
 * Classifies the actor of one log row.
 *
 * `f_api_logs.user_id` is `ON DELETE SET NULL` and the join back to the users
 * table is deliberately a LEFT join, so a request made by a since-deleted
 * account still appears — with `id` and `name` null but `actor_email` intact.
 * That email is a denormalised copy written at request time for exactly this
 * reason: the trail has to survive the account. Such a row is `'deleted'`, and
 * rendering it as "Anonymous" throws away the one piece of identity the schema
 * went out of its way to preserve.
 *
 * Only `actor === null` — a request that carried no token — is `'anonymous'`.
 * Any other combination is `'unknown'` rather than a guess, so a contract
 * change surfaces as an honest gap instead of a plausible-looking lie.
 */
export function actorState(actor: ApiLogActor | null): SimpleActorState {
  if (actor === null) return { kind: 'anonymous' };
  if (actor.id !== null && actor.name !== null) {
    return { kind: 'user', id: actor.id, name: actor.name, email: actor.email };
  }
  if (actor.email !== null && actor.email !== '') {
    return { kind: 'deleted', email: actor.email };
  }
  return { kind: 'unknown' };
}

/* ------------------------------------------------------------------ *
 * Support sessions
 * ------------------------------------------------------------------ */

/**
 * ⚠️ THE ONE SWAP POINT for the support-session contract — now NARROWED to the
 * shipped shape.
 *
 * ✅ Reconciled 2026-08-30 against `emerge-backend-app`: migration 110,
 * `api-logs.repository.ts` (`toSupport`, and the `SUPPORT_ACTOR_JOIN` behind
 * it) and `docs/api/api-logs.md`. An earlier build could not know which of two
 * shapes would arrive and read BOTH; the answer is settled, and carrying the
 * dead half would leave a branch nothing can reach and nothing can test.
 *
 * ```jsonc
 * "support": {
 *   "sessionId": 42,
 *   "startedBy": { "id": 1, "name": "Moin", "email": "moin@…" }
 * }
 * ```
 *
 * ## ⚠️ `actor` IS THE TARGET, and `support.startedBy` is the operator
 *
 * `actor` is the account the request RAN IN — in the feature's own vocabulary
 * the `actingAs` account — because that is the identity every guard evaluated
 * and the one written into `createdById` on any record the request touched.
 * `support.startedBy` is the super administrator who was really at the
 * keyboard. **Both are true and both must be rendered.** A row shown as
 * *"Anvita deleted invoice 12"* when a super administrator did it while acting
 * as Anvita is a forgery the screen produced, and it is the one thing this
 * feature must never cause.
 *
 * ⚠️ **There is deliberately NO `support.actingAs`, and asking for one would be
 * asking for a defect.** That is exactly `actor`; a second copy of one fact is
 * two fields that can disagree, and on an audit row that means a dispute with
 * two answers in it.
 *
 * ⚠️ **The marker is `support !== null` — NEVER `support.startedBy.id !==
 * null`.** `support_started_by_id` is `ON DELETE SET NULL` while
 * `support_started_by_email` is the denormalised copy that survives, so testing
 * the id turns every historical support row back into an ordinary action by the
 * target the day that super administrator's account is deleted. `name` comes
 * from a join and goes with the id.
 *
 * ⚠️ Do not spread these strings across call sites. A wire key repeated in four
 * files is four places to miss if it is ever renamed, and the miss is silent: an
 * unread key is `undefined`, and a support session whose marker went unread
 * renders as an ordinary action by the target — the exact forgery above.
 */
export const SUPPORT_SESSION_WIRE = {
  /**
   * The key whose PRESENCE on a row means "this happened inside a support
   * session". Presence is the whole marker: a row is still recognised as one
   * even when neither person behind it can be named.
   */
  marker: 'support',
  /** Within `support`: the person who was ACTING. The target is `actor`, on the row itself. */
  operator: 'startedBy',
} as const;

/** Reads one loosely-typed wire value as an actor, or null when it names nobody. */
function readActor(value: unknown): ApiLogActor | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;

  const id = typeof record.id === 'number' && Number.isFinite(record.id) ? record.id : null;
  const name = typeof record.name === 'string' ? record.name : null;
  const email = typeof record.email === 'string' ? record.email : null;

  if (id === null && name === null && email === null) return null;
  return { id, name, email };
}

/**
 * Classifies one audit row, support sessions included.
 *
 * `actorState` cannot answer this on its own: it is handed the actor alone,
 * while the second person in a support session is recorded BESIDE the actor on
 * the row. Reading the row is therefore the only way to see both.
 *
 * Takes `unknown` deliberately, and keeps doing so now that `ApiLogSummary`
 * DOES declare `support`. The parameter is not the point of the looseness: this
 * is the one function that has to survive the row being a shape it does not
 * expect — an older server that omits the key, a newer one that renames it —
 * and a typed parameter would let a call site's cast decide that question
 * instead. Every value is narrowed before use and nothing here throws.
 *
 * ⚠️ THE MARKER WINS, ALWAYS. If a support-session key is present but the people
 * behind it cannot be resolved, this still returns `'support-session'` with the
 * unnameable side as `'unknown'`. Degrade the NAME, never the STATE: a row that
 * says "someone this row does not name" is visibly incomplete and gets
 * reported, whereas one that quietly falls through to the ordinary branch reads
 * as a finished, ordinary action and never gets reported at all.
 */
export function rowActorState(row: unknown): ActorState {
  const support = readSupportSession(row);
  if (support !== null) return support;

  const record = (row ?? {}) as { actor?: ApiLogActor | null };
  return actorState(record.actor ?? null);
}

/**
 * The support-session half of `rowActorState`, split out so the shape mapping
 * lives in one readable place beside the constant it reads.
 *
 * Returns null for an ordinary row — the overwhelming majority — so the caller
 * falls straight through to `actorState`.
 */
function readSupportSession(
  row: unknown,
): Extract<ActorState, { kind: 'support-session' }> | null {
  if (typeof row !== 'object' || row === null) return null;
  const record = row as Record<string, unknown>;

  /*
   * ⚠️ THE MARKER IS THE PRESENCE OF THE OBJECT, and nothing inside it. `null`
   * on every ordinary request, an object on every support one — so this is the
   * whole test, and it is deliberately NOT `support.startedBy.id !== null`,
   * which would reclassify every historical support row as an ordinary action
   * the day that super administrator's account is deleted.
   */
  const support = record[SUPPORT_SESSION_WIRE.marker];
  if (typeof support !== 'object' || support === null) return null;

  /*
   * `actor` is the TARGET — the account the request ran in — and
   * `support.startedBy` is the operator. There is no second shape to detect any
   * more, and no `support.actingAs` to prefer over `actor`: the server does not
   * send one, on purpose.
   *
   * Both sides route through `actorState`, so each inherits the deleted-account
   * handling for free — a past session whose target, or whose operator, has
   * since been deleted still names them by the email the schema keeps for
   * exactly that purpose, rather than dropping to "Anonymous".
   */
  const operator = readActor((support as Record<string, unknown>)[SUPPORT_SESSION_WIRE.operator]);

  return {
    kind: 'support-session',
    operator: partyState(operator),
    target: partyState(readActor(record.actor)),
  };
}

/**
 * `actorState` for one PARTY of a support session.
 *
 * The single difference is what a missing person means. On an ordinary row a
 * null actor is `'anonymous'` — a request that carried no token, which is a real
 * and useful fact. Inside a support session it cannot be: both parties were
 * signed in by definition, since the session could not have been opened
 * otherwise. So a party we cannot name is `'unknown'` — "this row does not say"
 * — and never "Someone not signed in", which would be a fact invented to fill a
 * gap, on the one screen where inventing facts is most expensive.
 */
function partyState(actor: ApiLogActor | null): SimpleActorState {
  return actor === null ? { kind: 'unknown' } : actorState(actor);
}

/**
 * The one-line label for an actor state.
 *
 * A deleted user reads as their email — it is the only identity left, and it is
 * still enough to search on through the `actorEmail` filter. Callers are
 * expected to pair this with a visual marker for `'deleted'`; the text alone
 * cannot say "this account is gone".
 *
 * A support session names BOTH people, because there is no such thing as a
 * one-line label for one of them: naming either alone is the misattribution the
 * state exists to prevent, and a caller that only had room for one name would
 * reach for the target and produce exactly that.
 */
export function actorLabel(state: ActorState): string {
  switch (state.kind) {
    case 'anonymous':
      return 'Anonymous';
    case 'user':
      return state.name;
    case 'deleted':
      return state.email;
    case 'support-session':
      return `${partyLabel(state.operator)}, acting as ${partyLabel(state.target)}`;
    default:
      return 'Unknown';
  }
}

/**
 * One party of a support session, for the compact label above.
 *
 * `'unknown'` reads as "an account this row does not name" rather than the bare
 * "Unknown", because the sentence has to survive being read aloud in a dispute
 * about who changed something. "Unknown, acting as Anvita Shetty" invites the
 * reader to treat the first half as noise; saying the row does not name them
 * makes the gap the finding it actually is.
 */
function partyLabel(state: SimpleActorState): string {
  switch (state.kind) {
    case 'user':
      return state.name;
    case 'deleted':
      return `a deleted account (${state.email})`;
    default:
      return 'an account this row does not name';
  }
}

/**
 * Badge tone for an HTTP status code.
 *
 * A 4xx is the caller's mistake and a 5xx is ours, so they get different tones
 * rather than one shared error red — scanning a page of logs for the rows that
 * are actually our fault is the main thing this view is for. A 3xx is a normal
 * outcome, not a problem, so it reads as informational. Anything outside
 * 2xx–5xx (a 1xx, or a bad value) falls back to neutral instead of asserting a
 * meaning it does not have.
 */
export function statusTone(statusCode: number): BadgeTone {
  if (statusCode >= 200 && statusCode < 300) return 'success';
  if (statusCode >= 300 && statusCode < 400) return 'info';
  if (statusCode >= 400 && statusCode < 500) return 'warning';
  if (statusCode >= 500 && statusCode < 600) return 'danger';
  return 'neutral';
}

/**
 * Renders `durationMs` for a log row.
 *
 * Named `formatLatency` and not `formatDuration` on purpose: `lib/format.ts`
 * already owns `formatDuration` for a deliverable's span in DAYS, and two
 * different units behind one name is how a cell ends up reading "842 days".
 *
 * Sub-second values keep their milliseconds because that is the resolution
 * anyone tuning an endpoint cares about; a second or more collapses to one
 * decimal, where the extra digits are noise.
 */
export function formatLatency(ms: number): string {
  if (!Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Renders `meta.total` honestly.
 *
 * The count query stops at 10,000 rows. When `totalIsExact` is false the total
 * is exactly 10000 and is a FLOOR, not a count — printing "10,000" then is a
 * lie, and "of 10,000" in a pager is a worse one. The same cap propagates into
 * `totalPages`, so a pager that shows a last-page number has to run that figure
 * through here too rather than trusting it.
 */
export function formatLogTotal(total: number, totalIsExact: boolean): string {
  const grouped = total.toLocaleString('en-US');
  return totalIsExact ? grouped : `${grouped}+`;
}

/**
 * Today as 'YYYY-MM-DD' built from LOCAL calendar parts.
 *
 * Re-exported from `invoice-utils` rather than reimplemented so the app keeps
 * exactly one definition of "today". `toISOString().slice(0, 10)` is UTC and
 * hands back tomorrow's date for anyone east of Greenwich in their evening,
 * which would silently shift every default date filter on this screen by a day.
 */
export function todayLocal(): string {
  return todayIsoDate();
}

/**
 * The local calendar date `days` before today, as 'YYYY-MM-DD'.
 *
 * The stats endpoint defaults to a 7-day window and the list screen offers the
 * usual 24h / 7d / 30d presets, so this is the `from` side of all of them.
 * Built on `addDaysIso`, which does the month-length and leap-year arithmetic
 * through local `Date` parts and never touches UTC.
 */
export function daysAgoLocal(days: number): string {
  return addDaysIso(todayIsoDate(), -days);
}

/**
 * The day after a 'YYYY-MM-DD', for building an INCLUSIVE `to` bound.
 *
 * The server compares `created_at <= :to`, and a bare date is parsed as
 * midnight — so `to=2026-08-24` means "up to the very start of the 24th" and
 * drops that entire day's traffic. A filter that means "through the 24th" has
 * to send the 25th. The arithmetic runs on the calendar parts via `addDaysIso`;
 * round-tripping through a UTC `Date` would reintroduce the very off-by-one
 * this exists to fix.
 */
export function nextDayLocal(ymd: string): string {
  return addDaysIso(ymd, 1);
}

/**
 * Detects the truncation marker that replaces an oversized logged body.
 *
 * A request or response body over 65536 bytes is swapped out WHOLESALE for
 * `{ _truncated: true, _bytes, _note }` — there is no partial fragment behind
 * it to show. Without this check a viewer renders a meaningless three-key JSON
 * tree and the reader concludes the request really did send
 * `{"_truncated": true}`. Detect it and say "body too large to store" instead.
 */
export function isTruncatedBody(
  value: unknown,
): value is { _truncated: true; _bytes: number; _note: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { _truncated?: unknown })._truncated === true
  );
}

/**
 * Pretty-prints a logged body for display, or `null` when there is nothing.
 *
 * The three body columns are typed `unknown` because MariaDB hands JSON columns
 * back as strings while MySQL hands back objects, and content that failed to
 * parse is returned raw — so a string here is a legitimate value, not a bug. A
 * string that parses as JSON is formatted; one that does not is returned
 * verbatim rather than quoted and escaped into gibberish.
 *
 * Never throws: `JSON.stringify` still dies on a circular structure or a
 * BigInt even after the server's own `[CIRCULAR]` pass, and a detail drawer
 * must not take the page down over one unreadable body.
 */
export function prettyJson(value: unknown): string | null {
  if (value === null || value === undefined) return null;

  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * Explains the placeholders a reader will meet inside a logged body.
 *
 * Shown alongside any rendered body, because every one of these is a value the
 * server substituted — not something the client actually sent. The redaction is
 * a deliberate substring over-match on the lowercased key name, so innocuous
 * fields get caught as well; saying so up front is what stops someone filing
 * "the API is corrupting sortKey" as a bug.
 */
export const REDACTION_NOTE =
  "Logged bodies are sanitised: '[REDACTED]' replaces the value of any key whose lowercased name contains password, passwd, hash, token, secret, authorization, auth, cookie, credential, apikey, api_key, key, signature, otp or pin — a deliberate substring over-match, so a field named sortKey comes back redacted too — while '[CIRCULAR]', '[TRUNCATED: max depth]' and '[BINARY: N bytes]' mark a repeated reference, a structure nested too deeply to walk, and a binary buffer.";

/**
 * The exact method values the `method` filter accepts.
 *
 * Hard-coded from the contract's enum rather than harvested from whichever
 * methods happen to appear in the current page of results — a filter built out
 * of the visible rows can never find the row you are looking for.
 */
export const HTTP_METHODS: readonly string[] = [
  'GET',
  'HEAD',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
  'OPTIONS',
];

/**
 * The action slugs the `actionSlug` filter accepts.
 *
 * This is the audit's own vocabulary, not a set of HTTP verbs — 'convert' and
 * 'export' have no method that implies them, so they must be listed explicitly
 * or they become unfilterable.
 *
 * ⛔ **THE AUTHORITY IS `actionFor()` IN THE BACKEND'S `api-logger.ts`, NOT
 * `f_actions`.** The two differ by one member and reaching for the wrong one
 * puts a dead option in this list. `f_actions` has held **seven** rows since
 * migration 200 added `approve` for credit notes — but `actionFor()` returns
 * only read/create/update/delete/convert/export, so **`approve` can never be
 * stamped on a log row** (approving a credit note is a POST and files as
 * `create`). Offering it here would be a filter that always answers zero,
 * which reads as "nobody has approved anything" rather than as a control that
 * cannot work.
 *
 * ⚠️ **This is a COMPARISON taken on 2026-09-18, not a promise to stay in
 * step.** Re-take it by extracting the literals `actionFor()` can return and
 * diffing them against this array; a member the server can stamp and this list
 * lacks fails silently, because the server validates `actionSlug` as a free
 * `z.string()` and returns 200 for anything.
 */
export const ACTION_SLUGS: readonly string[] = [
  'read',
  'create',
  'update',
  'delete',
  'convert',
  'export',
];

/**
 * The module slugs the `moduleSlug` filter accepts.
 *
 * Note there is no slug here for /auth, /health or /permissions: those routes
 * sit outside the permission system and log `module: null`, which is a real
 * bucket rather than missing data — a "no module" option is a separate control,
 * not an entry in this list.
 *
 * ⛔ **THE AUTHORITY IS `MODULE_BY_SEGMENT` IN THE BACKEND'S `api-logger.ts`,
 * AND IT IS NOT `f_modules`.** `moduleFor()` answers
 * `MODULE_BY_SEGMENT[segment] ?? null`, so the set of values that can ever
 * reach `f_api_logs.module_slug` is exactly that map's distinct VALUES — a
 * closed set, which is the only reason a fixed list here can be complete at
 * all. **The permission catalogue is the wrong list in BOTH directions**: it
 * omits `api-keys`, `docusign`, `preferences`, `public` and `support-sessions`,
 * which are logged and carry no permission module; and it holds the four
 * `dashboards-*` and four `trackers-*` slugs, which no route stamps, so
 * offering those would put eight options here that always answer zero.
 *
 * 🔴 **THIS LIST WAS FROZEN AT MIGRATION 028'S ORIGINAL TWELVE AND 21 OF THE
 * SERVER'S 33 WERE UNFILTERABLE** — corrected 2026-09-18, after the identical
 * defect was found on the API-key scope picker. Nothing could see it: the
 * server validates `moduleSlug` as a free `z.string().max(60)` and answers 200
 * for anything, so there was no 422 to catch, and a module missing from a
 * dropdown is not a failure a person can report — they simply cannot ask the
 * question. Everything a person has done to a task, a note, a project, a
 * target, a credit note, a commission or a sales pursuit was in the log and
 * unreachable from the control built to find it.
 *
 * ⚠️ **This is a COMPARISON taken on 2026-09-18, not a promise to stay in
 * step.** Re-take it by extracting the distinct values of `MODULE_BY_SEGMENT`
 * and diffing them against this array — reading the two lists and judging them
 * equal is what let the last one rot. A slug the server can stamp and this list
 * lacks fails SILENTLY; a slug here the server never stamps is an option that
 * answers zero, which is the quieter half of the same mistake.
 */
export const MODULE_SLUGS: readonly string[] = [
  'deals',
  'quotes',
  'invoices',
  'credit-notes',
  'deliverables',
  'client-deliverables',
  'companies',
  'contacts',
  'projects',
  'products',
  'cycles',
  'intakes',
  'tasks',
  'notes',
  'sales-tracker',
  'invoice-tracker',
  'revenue-tracker',
  'accounts-dashboard',
  'targets',
  'target-periods',
  'commissions',
  'event-interest',
  'event-maps',
  'docusign',
  'users',
  'reference-data',
  'settings',
  'preferences',
  'onboarding-templates',
  'api-keys',
  'support-sessions',
  'public',
  'api-logs',
];
